import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { kr, dato, idag } from '../lib/format';
import { PageHeader, Card, Modal } from '../components/ui';
import { Plus, Pencil, Trash2, TrendingDown } from 'lucide-react';

const tom = () => ({
  navn: '', anskaffelsesdato: idag(), anskaffelsespris: 0, restverdi: 0,
  levetid_aar: 5, metode: 'lineær', saldo_sats: 20,
  konto_eiendel: '1230', konto_avskrivning: '6010', konto_akk_avskr: '1239',
  notater: '',
});

const Anleggsmidler = () => {
  const [liste, setListe] = useState([]);
  const [open, setOpen] = useState(false);
  const [redigerer, setRedigerer] = useState(null);
  const [skjema, setSkjema] = useState(tom());

  const last = async () => setListe((await api.get('/anleggsmidler')).data);
  useEffect(() => { last(); }, []);

  const aapneNy = () => { setRedigerer(null); setSkjema(tom()); setOpen(true); };
  const aapneRediger = (a) => { setRedigerer(a); setSkjema({ ...a }); setOpen(true); };

  const lagre = async () => {
    if (redigerer) await api.put(`/anleggsmidler/${redigerer.id}`, skjema);
    else await api.post('/anleggsmidler', skjema);
    setOpen(false); last();
  };

  const slett = async (a) => {
    if (!window.confirm(`Slett ${a.navn}?`)) return;
    try { await api.delete(`/anleggsmidler/${a.id}`); last(); }
    catch (e) { alert(e.response?.data?.error || 'Feil.'); }
  };

  const avskriv = async (a) => {
    const aar = window.prompt(`Bokfør avskrivning for år (${a.metode}):`, new Date().getFullYear());
    if (!aar) return;
    try {
      const r = await api.post(`/anleggsmidler/${a.id}/avskriv`, { aar });
      alert(`Avskrev ${kr(r.data.belop)} for ${aar}`);
      last();
    } catch (e) { alert(e.response?.data?.error || 'Feil.'); }
  };

  return (
    <>
      <PageHeader title="Anleggsmidler"
        subtitle="Maskiner, inventar og IT-utstyr med avskrivninger"
        actions={<button className="btn-primary" onClick={aapneNy}><Plus size={14} /> Nytt anleggsmiddel</button>} />

      <Card style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th>Navn</th><th>Anskaffet</th>
              <th style={{ textAlign: 'right' }}>Pris</th>
              <th style={{ textAlign: 'right' }}>Akk. avskr.</th>
              <th style={{ textAlign: 'right' }}>Bokført verdi</th>
              <th>Metode</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {liste.length === 0 && <tr><td colSpan={8} className="fm-text-muted" style={{ textAlign: 'center', padding: 16 }}>Ingen anleggsmidler.</td></tr>}
            {liste.map((a) => (
              <tr key={a.id}>
                <td><strong>{a.navn}</strong></td>
                <td>{dato(a.anskaffelsesdato)}</td>
                <td style={{ textAlign: 'right' }}>{kr(a.anskaffelsespris)}</td>
                <td style={{ textAlign: 'right' }}>{kr(a.akk_avskrivning)}</td>
                <td style={{ textAlign: 'right' }}><strong>{kr(a.bokfort_verdi)}</strong></td>
                <td><span className="badge badge-neutral">{a.metode}{a.metode === 'saldo' ? ` ${a.saldo_sats}%` : ` ${a.levetid_aar}å`}</span></td>
                <td><span className={`badge ${a.status === 'aktiv' ? 'badge-success' : 'badge-neutral'}`}>{a.status}</span></td>
                <td>
                  <div className="flex" style={{ gap: 4 }}>
                    <button className="btn-ghost" onClick={() => avskriv(a)} title="Bokfør årlig avskrivning"><TrendingDown size={12} /></button>
                    <button className="btn-ghost" onClick={() => aapneRediger(a)}><Pencil size={12} /></button>
                    <button className="btn-ghost" onClick={() => slett(a)} style={{ color: 'var(--theme-danger)' }}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)}
             title={redigerer ? `Rediger ${redigerer.navn}` : 'Nytt anleggsmiddel'}
             footer={<>
               <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
               <button className="btn-primary" onClick={lagre}>Lagre</button>
             </>}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1' }}><label>Navn</label>
            <input value={skjema.navn} onChange={(e) => setSkjema({ ...skjema, navn: e.target.value })} /></div>
          <div><label>Anskaffelsesdato</label>
            <input type="date" value={skjema.anskaffelsesdato} onChange={(e) => setSkjema({ ...skjema, anskaffelsesdato: e.target.value })} /></div>
          <div><label>Anskaffelsespris</label>
            <input type="number" value={skjema.anskaffelsespris} onChange={(e) => setSkjema({ ...skjema, anskaffelsespris: e.target.value })} /></div>
          <div><label>Restverdi</label>
            <input type="number" value={skjema.restverdi} onChange={(e) => setSkjema({ ...skjema, restverdi: e.target.value })} /></div>
          <div><label>Metode</label>
            <select value={skjema.metode} onChange={(e) => setSkjema({ ...skjema, metode: e.target.value })}>
              <option value="lineær">Lineær</option>
              <option value="saldo">Saldo</option>
            </select></div>
          {skjema.metode === 'lineær' ? (
            <div><label>Levetid (år)</label>
              <input type="number" value={skjema.levetid_aar} onChange={(e) => setSkjema({ ...skjema, levetid_aar: e.target.value })} /></div>
          ) : (
            <div><label>Saldosats (%)</label>
              <input type="number" value={skjema.saldo_sats} onChange={(e) => setSkjema({ ...skjema, saldo_sats: e.target.value })} /></div>
          )}
          <div><label>Eiendelskonto</label>
            <input value={skjema.konto_eiendel} onChange={(e) => setSkjema({ ...skjema, konto_eiendel: e.target.value })} /></div>
          <div><label>Avskrivningskonto</label>
            <input value={skjema.konto_avskrivning} onChange={(e) => setSkjema({ ...skjema, konto_avskrivning: e.target.value })} /></div>
          <div><label>Akk. avskr. konto</label>
            <input value={skjema.konto_akk_avskr} onChange={(e) => setSkjema({ ...skjema, konto_akk_avskr: e.target.value })} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label>Notater</label>
            <textarea rows={2} value={skjema.notater} onChange={(e) => setSkjema({ ...skjema, notater: e.target.value })} /></div>
        </div>
      </Modal>
    </>
  );
};

export default Anleggsmidler;
